import { useState, useEffect, useRef, forwardRef, type ReactNode } from "react";
import { Loader2, ChevronDown, ChevronRight, Wrench } from "lucide-react";
import { ToolCallCard } from "./ToolCallCard";
import type { LiveSessionMessage } from "@/lib/types";

type Props = {
  messages: LiveSessionMessage[];
  isAgentWorking: boolean;
  pullIndicatorSlot?: ReactNode;
};

const WORKING_MESSAGES = [
  "Thinking real hard...",
  "Crunching the bits...",
  "Hacking the mainframe...",
  "Consulting the oracle...",
  "Wrangling some code...",
  "Doing the thing...",
  "On it, boss...",
  "Making moves...",
  "Building something cool...",
  "Running calculations...",
  "Pulling strings...",
  "In the zone...",
  "Working the angles...",
  "Cooking up something good...",
  "Executing orders...",
  "Loading the cannons...",
  "Deploying brain cells...",
  "Reading the room...",
  "Going full send...",
  "Processing at warp speed...",
];

function AgentWorkingIndicator() {
  const [msgIndex, setMsgIndex] = useState(0);

  useEffect(() => {
    const initial = Math.floor(Math.random() * WORKING_MESSAGES.length);
    setMsgIndex(initial);
    const interval = setInterval(() => {
      setMsgIndex((prev) => (prev + 1) % WORKING_MESSAGES.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex items-center gap-2 text-primary/70">
      <Loader2 className="h-3.5 w-3.5 animate-spin" />
      <span className="text-xs">{WORKING_MESSAGES[msgIndex]}</span>
    </div>
  );
}

function parseToolMeta(msg: LiveSessionMessage) {
  const meta = msg.metadata as Record<string, unknown> | undefined;
  return {
    toolName: (meta?.toolName as string) ?? (meta?.tool_name as string) ?? "Tool",
    filePath: (meta?.filePath as string) ?? (meta?.file_path as string) ?? undefined,
  };
}

/** Truncate long text — shows first 6 lines with expand */
function TruncatedText({ content }: { content: string }) {
  const [expanded, setExpanded] = useState(false);
  const lines = content.split("\n");
  const isLong = lines.length > 8 || content.length > 400;

  if (!isLong || expanded) {
    return (
      <div className="text-zinc-200 whitespace-pre-wrap break-words text-xs sm:text-sm">
        {content}
      </div>
    );
  }

  const preview = lines.slice(0, 6).join("\n");
  return (
    <div>
      <div className="text-zinc-200 whitespace-pre-wrap break-words text-xs sm:text-sm">
        {preview}
      </div>
      <button
        onClick={() => setExpanded(true)}
        className="flex items-center gap-1 mt-1 text-[10px] text-primary hover:text-primary/80 transition-colors"
      >
        <ChevronDown className="h-3 w-3" />
        +{lines.length - 6} more lines
      </button>
    </div>
  );
}

/** Collapsed group of consecutive tool calls */
function ToolGroup({ messages }: { messages: LiveSessionMessage[] }) {
  const [expanded, setExpanded] = useState(false);
  const toolNames = messages
    .filter((m) => m.event_type === "tool_use")
    .map((m) => parseToolMeta(m).toolName);
  const unique = [...new Set(toolNames)];
  const summary = unique.length <= 3
    ? unique.join(", ")
    : `${unique.slice(0, 2).join(", ")} +${unique.length - 2} more`;

  return (
    <div className="rounded-md border border-zinc-800 bg-zinc-900/30 overflow-hidden my-0.5">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 w-full px-3 py-1.5 text-left hover:bg-zinc-800/50 transition-colors"
      >
        <ChevronRight
          className={`h-3 w-3 text-zinc-500 transition-transform shrink-0 ${expanded ? "rotate-90" : ""}`}
        />
        <Wrench className="h-3 w-3 text-zinc-500 shrink-0" />
        <span className="text-[10px] text-zinc-400">
          {toolNames.length} tool call{toolNames.length !== 1 ? "s" : ""}: {summary}
        </span>
      </button>
      {expanded && (
        <div className="border-t border-zinc-800 p-1.5 space-y-1">
          {messages.map((msg) => {
            const { toolName, filePath } = parseToolMeta(msg);
            return (
              <ToolCallCard
                key={msg.id}
                toolName={toolName}
                content={msg.content}
                filePath={filePath}
                isResult={msg.event_type === "tool_result"}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Group consecutive tool messages together */
function groupMessages(messages: LiveSessionMessage[]) {
  const groups: Array<{ type: "single"; msg: LiveSessionMessage } | { type: "tools"; msgs: LiveSessionMessage[] }> = [];
  let toolBuffer: LiveSessionMessage[] = [];

  const flushTools = () => {
    if (toolBuffer.length > 0) {
      groups.push({ type: "tools", msgs: [...toolBuffer] });
      toolBuffer = [];
    }
  };

  for (const msg of messages) {
    if (msg.event_type === "tool_use" || msg.event_type === "tool_result") {
      toolBuffer.push(msg);
    } else {
      flushTools();
      groups.push({ type: "single", msg });
    }
  }
  flushTools();

  return groups;
}

export const SessionTerminal = forwardRef<HTMLDivElement, Props>(
  function SessionTerminal({ messages, isAgentWorking, pullIndicatorSlot }, scrollRef) {
    const bottomRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages.length, isAgentWorking]);

    if (messages.length === 0 && !isAgentWorking) {
      return (
        <div ref={scrollRef} className="flex-1 overflow-y-auto bg-zinc-950 flex items-center justify-center">
          {pullIndicatorSlot}
          <p className="text-sm text-zinc-500 font-mono">Waiting for messages...</p>
        </div>
      );
    }

    const grouped = groupMessages(messages);

    return (
      <div ref={scrollRef} className="flex-1 overflow-y-auto bg-zinc-950 p-3 sm:p-4 font-mono text-sm space-y-1.5">
        {pullIndicatorSlot}
        {grouped.map((group, i) => {
          if (group.type === "tools") {
            return <ToolGroup key={`tg-${i}`} messages={group.msgs} />;
          }

          const msg = group.msg;

          // User message
          if (msg.role === "user" && msg.event_type === "text") {
            return (
              <div key={msg.id} className="flex gap-2">
                <span className="text-blue-400 font-semibold shrink-0 text-xs sm:text-sm">you:</span>
                <span className="text-zinc-200 whitespace-pre-wrap break-words text-xs sm:text-sm">
                  {msg.content}
                </span>
              </div>
            );
          }

          // Error
          if (msg.event_type === "error") {
            return (
              <div
                key={msg.id}
                className="rounded-md bg-red-950/50 border border-red-900 px-3 py-1.5 text-red-300 text-[10px] sm:text-xs whitespace-pre-wrap"
              >
                {msg.content}
              </div>
            );
          }

          // Status / system
          if (msg.role === "system" || msg.event_type === "status") {
            return (
              <div key={msg.id} className="text-[10px] text-zinc-500 italic">
                {msg.content}
              </div>
            );
          }

          // Assistant text
          return <TruncatedText key={msg.id} content={msg.content} />;
        })}

        {isAgentWorking && <AgentWorkingIndicator />}

        <div ref={bottomRef} />
      </div>
    );
  }
);
